import React, { useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, CheckCircle, Warning, Error as ErrorIcon } from '@mui/icons-material';
import type { Schema } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import type { SnackbarState } from './types';
import {
  findUnresolvedRefIds,
  hasAnyLevel,
  iterAllLevels,
  totalLevelCount,
  type OutputStructure,
} from '../../lib/outputStructure';

interface PromoteDraftDialogProps {
  open: boolean;
  onClose: () => void;
  selectedDraft: Schema | null;
  schemas: Schema[];
  /** @deprecated Use schemas instead */
  schemaSets?: Schema[];
  promoteForm: { name: string; schemaId: string };
  onPromoteFormChange: (form: { name: string; schemaId: string }) => void;
  promoteCreateNew: boolean;
  onPromoteCreateNewChange: (value: boolean) => void;
  promoteNewSchemaName: string;
  onPromoteNewSchemaNameChange: (value: string) => void;
  /** @deprecated Use promoteNewSchemaName / onPromoteNewSchemaNameChange */
  promoteNewSetName?: string;
  /** @deprecated Use onPromoteNewSchemaNameChange */
  onPromoteNewSetNameChange?: (value: string) => void;
  promoteCreating: boolean;
  onPromoteCreatingChange: (value: boolean) => void;
  onPromote: (schemaId: string, payload: any) => void;
  isPromoting: boolean;
  onSnackbar: (snackbar: NonNullable<SnackbarState>) => void;
}

const PromoteDraftDialog: React.FC<PromoteDraftDialogProps> = ({
  open,
  onClose,
  selectedDraft,
  schemas,
  schemaSets,
  promoteForm,
  onPromoteFormChange,
  promoteCreateNew,
  onPromoteCreateNewChange,
  promoteNewSchemaName,
  onPromoteNewSchemaNameChange,
  promoteNewSetName,
  onPromoteNewSetNameChange,
  promoteCreating,
  onPromoteCreatingChange,
  onPromote,
  isPromoting,
  onSnackbar,
}) => {
  const { user } = useAuth();
  const allSchemas = schemas ?? schemaSets ?? [];
  const newSchemaName = promoteNewSchemaName ?? promoteNewSetName ?? '';
  const handleNewSchemaNameChange = onPromoteNewSchemaNameChange ?? onPromoteNewSetNameChange ?? (() => {});
  const isDuplicatePromoteName = Boolean(promoteForm.name.trim()) && allSchemas.some(
    (schema) => schema.schema_name.toLowerCase() === promoteForm.name.trim().toLowerCase(),
  );
  const isDuplicateNewSchemaName = Boolean(newSchemaName.trim()) && allSchemas.some(
    (schema) => schema.schema_name.toLowerCase() === newSchemaName.trim().toLowerCase(),
  );

  // Pre-promotion validation for template schemas
  const templateValidation = useMemo(() => {
    const def = selectedDraft?.definition;
    if (!def?.output_structure) return null;

    const os = def.output_structure;
    const tables = def.tables || {};
    const checks: { label: string; status: 'pass' | 'warn' | 'block'; detail?: string }[] = [];

    // Check: OutputStructure defined (any level at any depth in the tree)
    const total = totalLevelCount(os as OutputStructure);
    const hasLevels = hasAnyLevel(os as OutputStructure);
    checks.push({
      label: 'Output structure defined',
      status: hasLevels ? 'pass' : 'block',
      detail: hasLevels ? `${total} levels` : 'No levels defined — run Interpret first',
    });

    // Check: Every level in the tree has a key column.
    const missingKeys: any[] = [];
    for (const lvl of iterAllLevels(os as OutputStructure)) {
      if (!lvl.key_column) missingKeys.push(lvl);
    }
    checks.push({
      label: 'All levels have key columns',
      status: missingKeys.length === 0 ? 'pass' : 'block',
      detail: missingKeys.length > 0
        ? `Level(s) missing key: ${missingKeys.map((l) => l.element_name).join(', ')}`
        : undefined,
    });

    // Check: At least one table has columns
    const tableCount = Object.keys(tables).length;
    const hasColumns = Object.values(tables).some(
      (t: any) => (t.columns || []).length > 0,
    );
    checks.push({
      label: 'At least one table has columns',
      status: tableCount > 0 && hasColumns ? 'pass' : 'block',
      detail: tableCount === 0 ? 'No tables — run Interpret first' : undefined,
    });

    // Check: Cross-reference integrity (#444 / VAL-4) — every ref_id
    // declared on an OutputStructure level must resolve to a key in
    // ``preserved_sections_context`` or to a ``label`` on one of the
    // ``preserved_sections`` blocks. Unresolved ref_ids are blocking
    // because the assembler / serializer will emit broken cross-refs
    // at runtime if promoted.
    const unresolved = findUnresolvedRefIds(os as OutputStructure);
    checks.push({
      label: 'All cross-references resolve',
      status: unresolved.length === 0 ? 'pass' : 'block',
      detail: unresolved.length === 0
        ? undefined
        : `Unresolved: ${unresolved
            .slice(0, 3)
            .map((u) => `${u.level} → ${u.ref_id}`)
            .join('; ')}${unresolved.length > 3 ? ` (+${unresolved.length - 3} more)` : ''}`,
    });

    // Check: Columns with no validation rules (warning only)
    const allColumns = Object.values(tables).flatMap(
      (t: any) => t.columns || [],
    );
    const validatorConfig = def.validator_config || {};
    const columnRules = validatorConfig.column_rules || {};
    const columnsWithoutRules = allColumns.filter(
      (c: any) => !columnRules[c.name],
    );
    if (columnsWithoutRules.length > 0) {
      checks.push({
        label: `${columnsWithoutRules.length} column(s) have no validation rules`,
        status: 'warn',
      });
    }

    const hasBlockers = checks.some((c) => c.status === 'block');
    return { checks, hasBlockers };
  }, [selectedDraft]);

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Promote Draft to Schema</DialogTitle>
      <DialogContent>
        {templateValidation && (
          <Box sx={{ mb: 2 }}>
            {templateValidation.hasBlockers && (
              <Alert severity="error" sx={{ mb: 1 }}>
                Template schema has blocking issues that must be resolved before promotion.
              </Alert>
            )}
            <Stack spacing={0.5}>
              {templateValidation.checks.map((check, idx) => (
                <Stack key={idx} direction="row" spacing={1} alignItems="center">
                  {check.status === 'pass' && <CheckCircle fontSize="small" color="success" />}
                  {check.status === 'warn' && <Warning fontSize="small" color="warning" />}
                  {check.status === 'block' && <ErrorIcon fontSize="small" color="error" />}
                  <Typography variant="body2" color={check.status === 'block' ? 'error.main' : 'text.primary'}>
                    {check.label}
                    {check.detail && (
                      <Typography component="span" variant="body2" color="text.secondary">
                        {' '}({check.detail})
                      </Typography>
                    )}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        )}

        <TextField
          margin="dense"
          label="Schema Name"
          fullWidth
          value={promoteForm.name}
          onChange={(e) => onPromoteFormChange({ ...promoteForm, name: e.target.value })}
          helperText={
            isDuplicatePromoteName
              ? 'A schema with this name already exists.'
              : 'Required. This becomes the target schema name.'
          }
          error={isDuplicatePromoteName}
        />
        <TextField
          margin="dense"
          label="Target Schema"
          select
          fullWidth
          value={promoteCreateNew ? '__create_new__' : promoteForm.schemaId}
          onChange={(e) => {
            if (e.target.value === '__create_new__') {
              onPromoteCreateNewChange(true);
              onPromoteFormChange({ ...promoteForm, schemaId: '' });
            } else {
              onPromoteCreateNewChange(false);
              handleNewSchemaNameChange('');
              onPromoteFormChange({ ...promoteForm, schemaId: e.target.value });
            }
          }}
          helperText={
            promoteCreateNew
              ? 'A new schema will be created.'
              : !promoteForm.schemaId && !selectedDraft?.schema_id
                ? 'Required. Please select a target schema.'
                : 'Select the target schema for promotion.'
          }
          error={!promoteCreateNew && !promoteForm.schemaId && !selectedDraft?.schema_id}
        >
          <MenuItem value="__create_new__">
            <Stack direction="row" spacing={1} alignItems="center">
              <AddIcon fontSize="small" color="primary" />
              <Typography variant="body2" color="primary">Create New Schema</Typography>
            </Stack>
          </MenuItem>
          <Divider />
          <MenuItem value="">
            <em>Select schema</em>
          </MenuItem>
          {allSchemas.map((s) => (
            <MenuItem key={s.schema_id} value={s.schema_id}>
              {s.schema_name} ({s.schema_id.slice(0, 8)})
            </MenuItem>
          ))}
        </TextField>
        {promoteCreateNew && (
          <TextField
            margin="dense"
            label="New Schema Name"
            fullWidth
            value={newSchemaName}
            onChange={(e) => handleNewSchemaNameChange(e.target.value)}
            helperText={
              isDuplicateNewSchemaName
                ? 'A schema with this name already exists.'
                : 'Required. Name for the new schema.'
            }
            error={isDuplicateNewSchemaName}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={promoteCreating}>Cancel</Button>
        <Button
          onClick={async () => {
            if (!selectedDraft) return;
            // "Create New" simply renames the draft and promotes it —
            // no separate schema row is created.
            const targetSchemaId = promoteCreateNew
              ? selectedDraft.schema_id
              : (promoteForm.schemaId || selectedDraft.schema_id);
            const schemaName = promoteCreateNew
              ? newSchemaName.trim()
              : promoteForm.name;
            onPromote(selectedDraft.schema_id, {
              schemaName,
              schemaId: targetSchemaId,
              approvedBy: user?.email ?? 'web-ui',
              status: 'active',
            });
          }}
          variant="contained"
          disabled={
            isPromoting ||
            promoteCreating ||
            !promoteForm.name.trim() ||
            isDuplicatePromoteName ||
            (templateValidation?.hasBlockers ?? false) ||
            (promoteCreateNew
              ? (!newSchemaName.trim() || isDuplicateNewSchemaName)
              : (!promoteForm.schemaId && !selectedDraft?.schema_id))
          }
        >
          {promoteCreating ? 'Creating Schema...' : isPromoting ? 'Promoting...' : 'Promote'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PromoteDraftDialog;
